import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MicIcon, ClockIcon, PlayIcon, PauseIcon, BookOpenIcon, FlameIcon, UsersIcon, MoonIcon, AlertCircleIcon, StarIcon } from 'lucide-react';
import { useRoleTheme } from '../../hooks/useRoleTheme';
const spiceLevels: Record<number, {
  label: string;
  color: string;
}> = {
  1: {
    label: 'Mild',
    color: 'bg-sky-500/30 text-sky-200'
  },
  2: {
    label: 'Warm',
    color: 'bg-amber-500/30 text-amber-200'
  },
  3: {
    label: 'Hot',
    color: 'bg-orange-500/30 text-orange-200'
  },
  4: {
    label: 'Steamy',
    color: 'bg-rose-500/30 text-rose-200'
  },
  5: {
    label: 'Scorching',
    color: 'bg-red-500/30 text-red-200'
  }
};
const darknessLevels: Record<number, {
  label: string;
  color: string;
}> = {
  1: {
    label: 'Shadow',
    color: 'bg-slate-400/30 text-slate-200'
  },
  2: {
    label: 'Grey',
    color: 'bg-slate-500/30 text-slate-200'
  },
  3: {
    label: 'Abyss',
    color: 'bg-gray-600/30 text-gray-200'
  },
  4: {
    label: 'Pitch Black',
    color: 'bg-gray-700/40 text-gray-100'
  },
  5: {
    label: 'The Void',
    color: 'bg-gray-800/50 text-white'
  }
};
const getGenreColor = (genre: string): string => {
  const g = genre.toLowerCase();
  if (g.includes('romance')) return 'bg-pink-600';
  if (g.includes('fantasy')) return 'bg-indigo-600';
  if (g.includes('sci-fi') || g.includes('science')) return 'bg-blue-600';
  if (g.includes('mystery')) return 'bg-indigo-600';
  if (g.includes('thriller')) return 'bg-red-600';
  if (g.includes('horror')) return 'bg-gray-800';
  if (g.includes('historical')) return 'bg-amber-600';
  if (g.includes('young adult') || g.includes('ya')) return 'bg-cyan-600';
  if (g.includes('contemporary')) return 'bg-emerald-600';
  return 'bg-violet-600';
};
const getStatusColor = (status: string, isNew?: boolean, isDebut?: boolean): string => {
  if (isDebut) return 'bg-fuchsia-600';
  if (isNew) return 'bg-indigo-500';
  const s = status?.toLowerCase() || '';
  if (s === 'completed') return 'bg-green-600';
  if (s === 'ongoing') return 'bg-yellow-500';
  if (s.includes('hiatus') || s.includes('discontinued')) return 'bg-red-600';
  return 'bg-blue-600';
};
interface AudiobookCardProps {
  title: string;
  author: string;
  coverImage: string;
  narrators: string[];
  audioLength: string;
  genre: string;
  relationshipTypes?: string[];
  audioSampleUrl: string;
  isPlaying: boolean;
  onPlayPause: (title: string) => void;
  spiceLevel?: number;
  darknessLevel?: number;
  tropes?: string[];
  seriesName?: string;
  seriesNumber?: number;
  seriesStatus?: string;
  totalSeriesBooks?: number;
  endingType?: string;
  contentWarnings?: string[];
  isNew?: boolean;
  isDebut?: boolean;
  darkMode?: boolean;
}
const AudiobookCard: React.FC<AudiobookCardProps> = ({
  title,
  author,
  coverImage,
  narrators,
  audioLength,
  genre,
  relationshipTypes,
  audioSampleUrl,
  isPlaying,
  onPlayPause,
  spiceLevel,
  darknessLevel,
  tropes,
  seriesName,
  seriesNumber,
  seriesStatus,
  totalSeriesBooks,
  endingType,
  contentWarnings = [],
  isNew,
  isDebut,
  darkMode = false
}) => {
  const rt = useRoleTheme();
  const [imgError, setImgError] = useState(false);
  const fallbackImage = `https://images.unsplash.com/photo-1524578271613-d550eacf6090?w=400&h=600&fit=crop`;
  const authorSlug = author.toLowerCase().replace(/\s+/g, '-');
  const seriesSlug = seriesName ? seriesName.toLowerCase().replace(/\s+/g, '-') : '';
  const bookSlug = title.replace(/\s+/g, '-').toLowerCase();
  const narratorCount = narrators.length;
  const isDuet = narratorCount === 2;
  const isMulti = narratorCount > 2;
  const hasContentWarnings = contentWarnings.length > 0;
  const hasSeriesInfo = !!seriesName;
  const isLongSeriesName = seriesName && seriesName.length > 16;
  const getStatusLabel = () => {
    if (isDebut) return 'DEBUT';
    if (isNew) return 'NEW';
    if (!seriesStatus) return null;
    return seriesStatus.charAt(0).toUpperCase() + seriesStatus.slice(1);
  };
  const statusLabel = getStatusLabel();
  return <div className="w-full group relative" data-id="element-892" data-mp="3ag3kf1wra820">
      <Link to={`/book/${bookSlug}`} data-id="element-893" data-mp="3ah4vh6druujq">
        <div className="relative w-full aspect-square rounded-lg shadow-md overflow-hidden transition-transform duration-200 group-hover:shadow-lg group-hover:scale-105" data-id="element-894" data-mp="3oetkmqevgnul">
          {/* Cover image */}
          {imgError ? <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-indigo-100 via-indigo-200 to-indigo-300" data-id="element-895" data-mp="19c195wpo5np">
              <span className="text-indigo-500 text-xs font-medium text-center px-2 line-clamp-2" data-id="element-896" data-mp="3tcd4q6y93uug">
                {title}
              </span>
            </div> : <img src={coverImage || fallbackImage} alt={title} className="w-full h-full object-cover" onError={() => setImgError(true)} data-id="element-897" data-mp="61vmmp1n4532" />}

          {/* Genre tag — top left, solid color (matches UnifiedBookCard) */}
          {genre && <span className={`absolute top-2 left-2 text-white text-xs ${getGenreColor(genre)} px-2 py-0.5 rounded-full z-10`} data-id="element-898" data-mp="zj8qg1pk3anh">
              {genre}
            </span>}

          {/* Status badge — top right (series status / new / debut) */}
          {statusLabel && <span className={`absolute top-2 right-2 text-white text-xs ${getStatusColor(seriesStatus || '', isNew, isDebut)} px-2 py-0.5 rounded-full z-10`} data-id="element-899" data-mp="fx3vjqrep5fa">
              {statusLabel}
            </span>}

          {/* Content warning icon — bottom left of cover */}
          {hasContentWarnings && <span className="absolute bottom-2 left-2 inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-500 text-white shadow-md z-10" title={`Content Warnings: ${contentWarnings.join(', ')}`} data-id="element-900" data-mp="bwaad55cm4q3">
              <AlertCircleIcon className="h-3.5 w-3.5" data-id="element-901" data-mp="3extygn4z7dk0" />
            </span>}

          {/* Hover overlay */}
          <div className="absolute inset-0 bg-black/85 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex flex-col p-3 z-20" data-id="element-902" data-mp="3a73tvc4djeqd">
            <div className="min-w-0" data-id="element-903" data-mp="355xyf6jbxdd5">
              {/* Title + series number */}
              <div className="flex items-center justify-between mb-0.5" data-id="element-904" data-mp="30b0ps0pyp60s">
                <h3 className="font-bold text-white text-sm line-clamp-1" data-id="element-905" data-mp="1q92otwxvis4">
                  {title}
                </h3>
                {seriesNumber && !isLongSeriesName && <span className="ml-2 flex-shrink-0 text-[10px] bg-indigo-500/80 text-white px-1.5 py-0.5 rounded" data-id="element-906" data-mp="3nxz5gw69c9xn">
                    {totalSeriesBooks ? `${seriesNumber}/${totalSeriesBooks}` : `Bk ${seriesNumber}`}
                  </span>}
              </div>

              {/* Series number on next line for long names */}
              {seriesNumber && isLongSeriesName && <span className="inline-block text-[10px] bg-indigo-500/80 text-white px-1.5 py-0.5 rounded mb-1" data-id="element-907" data-mp="n0eph6qsvs3n">
                  {totalSeriesBooks ? `Book ${seriesNumber}/${totalSeriesBooks}` : `Book ${seriesNumber}`}
                </span>}

              {/* Author */}
              <p className="text-white/80 text-xs line-clamp-1 mb-1.5" data-id="element-908" data-mp="16qqrxmgxcnzb">
                by{' '}
                <Link to={`/author/${authorSlug}`} className="hover:text-indigo-300 hover:underline transition-colors" onClick={e => e.stopPropagation()} data-id="element-909" data-mp="1arshtcoybnom">
                  {author}
                </Link>
              </p>

              {/* Series name (linked) */}
              {hasSeriesInfo && <div className="text-[11px] text-indigo-300 flex items-center mb-1.5" data-id="element-910" data-mp="3hkmn7w5hrg0q">
                  <BookOpenIcon className="h-3 w-3 mr-1 flex-shrink-0" data-id="element-911" data-mp="3mhv3tsazrbzv" />
                  <Link to={`/series/${seriesSlug}`} className="line-clamp-1 hover:text-indigo-200 transition-colors" onClick={e => e.stopPropagation()} data-id="element-912" data-mp="1s8ppu6y6pe14">
                    {seriesName}
                  </Link>
                </div>}

              {/* Tags: relationship type, ending type, narrator type */}
              <div className="mb-1.5 flex flex-wrap gap-1" data-id="element-913" data-mp="19bohqd9c2xsy">
                {relationshipTypes && relationshipTypes.length > 0 && <span className="px-1.5 py-0.5 bg-pink-500/80 text-white rounded text-[10px]" data-id="element-914" data-mp="14e6gza5gzow1">
                    {relationshipTypes[0]}
                  </span>}
                {endingType && <span className="px-1.5 py-0.5 bg-green-600/80 text-white rounded text-[10px]" data-id="element-915" data-mp="2uo83zpqoq193">
                    {endingType}
                  </span>}
                {isDuet && <span className="px-1.5 py-0.5 bg-indigo-500/80 text-white rounded text-[10px]" data-id="element-916" data-mp="2x8xnj7hh840i">
                    Duet
                  </span>}
                {isMulti && <span className="px-1.5 py-0.5 bg-violet-600/80 text-white rounded text-[10px]" data-id="element-917" data-mp="2o897k78ivtkz">
                    Multi
                  </span>}
              </div>

              {/* Narrator display */}
              <div className="text-[11px] text-gray-300 flex items-center mb-1 relative z-30" data-id="element-918" data-mp="3fyvwrqx0g35q">
                {narratorCount > 1 ? <UsersIcon className="h-3 w-3 mr-1 flex-shrink-0 text-indigo-400" data-id="element-919" data-mp="3kxgthgd204pf" /> : <MicIcon className="h-3 w-3 mr-1 flex-shrink-0 text-indigo-400" data-id="element-920" data-mp="374r7vd1ft1su" />}
                <span className="line-clamp-1" data-id="element-921" data-mp="3b6cjyw65dc1z">
                  {isDuet ? <>
                      {narrators[0]} & {narrators[1]}
                    </> : isMulti ? <div className="group/tooltip relative inline-block" data-id="element-922" data-mp="izr6otce4oup">
                      <span className="hover:text-indigo-300 transition-colors cursor-pointer" data-id="element-923" data-mp="ixo83kx69ned">
                        {narrators[0]} + {narratorCount - 1} more
                      </span>
                      <div className="absolute left-0 top-full mt-1 w-48 p-2 bg-gray-900/95 border border-gray-700 rounded-md shadow-xl opacity-0 invisible group-hover/tooltip:opacity-100 group-hover/tooltip:visible transition-all duration-200 z-40" data-id="element-924" data-mp="91bfyrh9zhlq">
                        <div className="flex flex-col gap-1" data-id="element-925" data-mp="50hu03ne8cvh">
                          <span className="text-[10px] uppercase text-gray-500 font-semibold px-1" data-id="element-926" data-mp="51sv1vpgf6iw">
                            Narrators
                          </span>
                          {narrators.map(narrator => <span key={narrator} className="text-gray-300 hover:text-indigo-300 transition-colors px-1 py-0.5 rounded hover:bg-white/5 cursor-default block truncate" data-id="element-927" data-mp="3df9kn7w1uxar">
                              {narrator}
                            </span>)}
                        </div>
                      </div>
                    </div> : <span data-id="element-928" data-mp="32unzq3jrk4tv">{narrators[0]}</span>}
                </span>
              </div>

              {/* Audio length */}
              <div className="text-[11px] text-gray-300 flex items-center mb-1" data-id="element-929" data-mp="3hqwyz53nmi6a">
                <ClockIcon className="h-3 w-3 mr-1 flex-shrink-0 text-indigo-400" data-id="element-930" data-mp="3ctnbhuztsgl3" />
                <span data-id="element-931" data-mp="3qk0hvsx2jn8b">{audioLength}</span>
              </div>

              {/* Content warnings icon in overlay */}
              {hasContentWarnings && <div className="flex items-center mb-1" data-id="element-932" data-mp="3mjgl4qgeqlmp">
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-500/30 text-amber-200" title={`Content Warnings: ${contentWarnings.join(', ')}`} data-id="element-933" data-mp="4c90trn3do6p">
                    <AlertCircleIcon className="h-3 w-3" data-id="element-934" data-mp="2t3ueggsagakz" />
                  </span>
                </div>}

              {/* Spice & Darkness levels */}
              <div className="mt-1 flex flex-wrap gap-1" data-id="element-935" data-mp="325ke29wbqftu">
                {spiceLevel && spiceLevel > 0 && spiceLevels[spiceLevel] && <span className={`inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 ${rt.badgeRadius} font-medium ${spiceLevels[spiceLevel].color}`} data-id="element-936" data-mp="36zeun4gmueop">
                    <FlameIcon className="h-2.5 w-2.5" data-id="element-937" data-mp="3gxcktxi1yljy" />
                    {spiceLevels[spiceLevel].label}
                  </span>}
                {darknessLevel && darknessLevel > 0 && darknessLevels[darknessLevel] && <span className={`inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 ${rt.badgeRadius} font-medium ${darknessLevels[darknessLevel].color}`} data-id="element-938" data-mp="1qkwuymvx1sos">
                      <MoonIcon className="h-2.5 w-2.5" data-id="element-939" data-mp="1o0qtcluryebe" />
                      {darknessLevels[darknessLevel].label}
                    </span>}
              </div>
            </div>

            {/* Play button — bottom right */}
            <div className="mt-auto flex justify-end" data-id="element-940" data-mp="19aj6ltxqi94w">
              <button onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              onPlayPause(title);
            }} className="p-2 rounded-full bg-indigo-600 text-white hover:bg-indigo-700 transition-colors shadow-lg transform hover:scale-105 active:scale-95" aria-label={isPlaying ? 'Pause' : 'Play'} data-id="element-941" data-mp="197opocdxj6g8">
                {isPlaying ? <PauseIcon className="h-4 w-4 fill-current" data-id="element-942" data-mp="ll8cuwesxf09" /> : <PlayIcon className="h-4 w-4 fill-current ml-0.5" data-id="element-943" data-mp="hic2fszf0p7e" />}
              </button>
            </div>
          </div>
        </div>
      </Link>
    </div>;
};
export default AudiobookCard;